import { NgForOf, NgIf } from '@angular/common';
import { MatOption } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { debounceTime, filter } from 'rxjs/operators';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelect, MatSelectChange, MatSelectModule, MatSelectTrigger } from '@angular/material/select';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { DsInputComponent } from '@design-system/ds-input/component/ds-input.component';
import { DsAbstractControlComponent } from '@abstract/components/ds-abstract-control.component';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  QueryList,
  Self,
  ViewChild,
} from '@angular/core';

/**
 * Reusable Angular Material select wrapper that integrates seamlessly
 * with reactive forms (`FormControl` / `NgControl`) and supports:
 * - Content projection of `MatOption` and `MatSelectTrigger`
 * - An `options` input — projected `mat-option` does not sync reliably with async lists; use `[options]` instead
 * - Optional clear-selection control via `isClearable` (`matSuffix` close icon)
 * - Custom `compareWith` function for object comparison
 * - ARIA attributes for accessibility
 * - Dynamic panel class styling
 * - Full ControlValueAccessor support for `formControl` and `formControlName`
 */
@Component({
  selector: 'ds-select',
  templateUrl: './ds-select.component.html',
  styleUrls: ['./ds-select.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- ControlValueAccessor relies on Default
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [NgIf, NgForOf, MatIconModule, MatSelectModule, ReactiveFormsModule, DsInputComponent],
})
export class DsSelectComponent extends DsAbstractControlComponent implements AfterViewInit, OnInit {
  @ViewChild(MatSelect) matSelect!: MatSelect;

  @ViewChild('searchInput') private readonly searchInput?: DsInputComponent;

  @ContentChildren(MatOption, { descendants: true }) contentOptions!: QueryList<MatOption>;

  @ContentChildren(MatSelectTrigger) contentTrigger!: QueryList<MatSelectTrigger>;

  /** Custom panel class for the dropdown */
  @Input() panelClass?: string | string[];

  /**
   * Whether the dropdown is searchable. When left unset, the search field is shown
   * automatically once the number of options exceeds `searchOptionThreshold`.
   * An explicit value always overrides the automatic behavior.
   */
  @Input() isSearchable?: boolean;

  /**
   * When true, shows a clear (close) icon in `matSuffix` while a value is selected.
   * Clicking it resets the control to `null` without opening the panel.
   */
  @Input() isClearable = false;

  /**
   * When true, the search is handled by the parent (server-side): the typed query is emitted via
   * `searchChange` and the component stops filtering `options` locally (they are expected to already
   * be filtered by the server). Supply the options through `[options]`.
   */
  @Input() isServerSearch = false;

  /** Whether the owner has a search request in flight; renders the searching row. */
  @Input() isSearching = false;

  /**
   * Emits the debounced, trimmed search query typed in the dropdown.
   *
   * In server mode it also emits `''` when the panel closes, so the owner can put its default list
   * back rather than leaving the last query's answers on offer the next time the panel opens.
   */
  @Output() searchChange = new EventEmitter<string>();

  /** Number of options above which the search field auto-enables */
  private readonly searchOptionThreshold = 5;

  /** Whether the search field is currently rendered */
  showSearch = false;

  /** Whether the bound control currently has a non-empty selection */
  hasSelection = false;

  /** Async option lists — avoids a sync issue with content-projected `mat-option`. Ignores projected content when set. */
  @Input() set options(options: BaseDropdownInterface[] | undefined) {
    if (options === undefined) {
      return;
    }

    this.hasOptionsInput = true;
    this.allOptions = options;
    this.filterInputOptions(this.appliedFilter);
    this.updateSearchVisibility();
    this.changeDetectorRef.markForCheck();

    if (this.matSelect) {
      this.refreshSelectionDisplay();
    }
  }

  /** Function to compare selected value with option value */
  @Input() compareWith?: (o1: unknown, o2: unknown) => boolean;

  /** Emits when the selection changes (mirrors MatSelect's selectionChange) */
  @Output() selectionChange = new EventEmitter<MatSelectChange>();

  /** Default comparison function if `compareWith` is not provided */
  readonly defaultCompareWith = (o1: unknown, o2: unknown): boolean => o1 === o2;

  /** Form control for search input */
  readonly autocompleteControl = new FormControl<string>('', { nonNullable: true });

  /** Indicates there are no options matching the current filter */
  noOptionsMatchingFilter = false;

  /** Indicates the option list is empty */
  noOptionsAvailable = false;

  /** Whether the select is disabled */
  disabled = false;

  /** Whether options are supplied through the `options` input */
  hasOptionsInput = false;

  /** Full list of options supplied through the `options` input */
  allOptions: BaseDropdownInterface[] = [];

  /** Options shown after applying the search filter */
  visibleOptions: BaseDropdownInterface[] = [];

  /**
   * The options actually rendered as `mat-option`s: {@link visibleOptions}, plus the control's own
   * value when no supplied option matches it.
   *
   * `MatSelect` renders the selected value only through a matched `mat-option` - its trigger text
   * is the matched option's `viewValue` - so a control holding a value the `options` input does not
   * offer would display the placeholder, silently looking unset. That happens routinely with a
   * value loaded from a record while the list is scoped to something narrower, and the answer is to
   * give that value an option of its own rather than to leave the field looking empty.
   */
  renderedOptions: BaseDropdownInterface[] = [];

  /** Trimmed search query currently applied to the option list */
  private appliedFilter = '';

  constructor(@Self() @Optional() public ngControl: NgControl) {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    if (!this.hasOptionsInput) {
      this.subscribeOnMatSelectOptionsChange();
      this.initProjectedOptions();
      this.subscribeOnContentOptionsChange();
    } else {
      this.refreshSelectionDisplay();
    }

    this.initTrigger();
    this.subscribeOnContentTriggerChange();
    this.updateSearchVisibility();
    this.subscribeOnPanelOpenChange();
  }

  ngOnInit(): void {
    this.syncHasSelection(this.control?.value);
    // A value the parent assigned before this subscription existed - its own `ngOnInit` runs
    // first - would otherwise never be offered an option of its own.
    this.applyRenderedOptions();
    this.subscribeOnControlValueChanges();

    const autocompleteSubscription = this.autocompleteControl.valueChanges.pipe(debounceTime(200)).subscribe((value: string): void => {
      this.applySearchQuery(value);
    });

    this.subscriptions.add(autocompleteSubscription);
  }

  /**
   * Runs a search query against the option list, skipping the pass when it is already applied.
   *
   * The applied query is tracked on the component rather than with `distinctUntilChanged`: the
   * search field is also cleared outside this stream (panel close), which leaves the operator
   * remembering a query that is no longer applied and makes it swallow the next identical one.
   * Typing hides that — every keystroke emits a different prefix — but pasting the same text emits
   * it in one go, and the list stays unfiltered.
   *
   * @param value - raw search text
   */
  private applySearchQuery(value: string): void {
    // Copied text routinely carries surrounding whitespace, which would match no option name.
    const query: string = (value ?? '').trim();

    if (query === this.appliedFilter) {
      return;
    }

    this.searchChange.emit(query);

    if (this.hasOptionsInput) {
      this.filterInputOptions(query);
      return;
    }

    this.applyProjectedOptionsFilter(query);
  }

  /**
   * TrackBy function for options supplied through the `options` input.
   *
   * @param _index - option index
   * @param option - dropdown option
   */
  trackByOptionId(_index: number, option: BaseDropdownInterface): string {
    return option.id;
  }

  /**
   * Clears the current selection without opening the select panel.
   *
   * @param event - mouse event from the clear icon
   */
  onClearSelection(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.disabled || !this.control || !this.hasSelection) {
      return;
    }

    this.control.setValue(null);
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.onTouched();
    this.changeDetectorRef.markForCheck();
  }

  writeValue(_obj: unknown): void {}

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Keeps `hasSelection` in sync with the bound form control value.
   */
  private subscribeOnControlValueChanges(): void {
    if (!this.control) {
      return;
    }

    const subscription = this.control.valueChanges.subscribe((value: unknown): void => {
      this.syncHasSelection(value);
      // A value assigned after the options landed - a form hydrated from a record, say - is exactly
      // the case that needs an option of its own.
      this.applyRenderedOptions();
      this.changeDetectorRef.markForCheck();
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Updates whether a clearable selection is currently present.
   *
   * @param value - current control value
   */
  private syncHasSelection(value: unknown): void {
    this.hasSelection = value !== null && value !== undefined && value !== '';
  }

  /**
   * Subscribes to panel open/close and manages search input focus and filter reset.
   * Focus/filter only apply while search is visible — subscription is always active so
   * late-enabled search (async options past the threshold) still autofocuses.
   */
  private subscribeOnPanelOpenChange(): void {
    const subscription = this.matSelect.openedChange.pipe(filter((): boolean => this.showSearch)).subscribe((isOpen: boolean): void => {
      if (isOpen) {
        this.focusSearchInput();
        return;
      }

      this.resetSearchFilter();

      // The empty filter is applied straight away below, so the debounced reset never reaches the
      // owner. In server mode the owner has to hear that the query is gone to restore its list.
      if (this.isServerSearch) {
        this.applySearchQuery('');
        return;
      }

      if (this.hasOptionsInput) {
        this.filterInputOptions('');
        return;
      }

      this.applyProjectedOptionsFilter('');
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Clears the search input.
   *
   * The change is emitted so that a query still sitting in the debounce window is superseded by the
   * reset instead of landing on the freshly cleared field. Callers apply the empty filter straight
   * away, so the debounced pass that follows finds nothing left to do.
   */
  private resetSearchFilter(): void {
    this.autocompleteControl.setValue('');
  }

  /**
   * Defers focus until the overlay panel and nested search input are rendered.
   */
  private focusSearchInput(): void {
    queueMicrotask((): void => {
      this.searchInput?.focus();
    });
  }

  /**
   * Subscribes to changes in the projected MatSelectTrigger elements
   * and updates the internal MatSelect trigger
   */
  private subscribeOnContentTriggerChange(): void {
    const subscription = this.contentTrigger.changes.subscribe((): void => {
      this.initTrigger();
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Subscribes to changes in the projected MatOption elements
   * and re-initializes the internal MatSelect options
   */
  private subscribeOnContentOptionsChange(): void {
    const subscription = this.contentOptions.changes.subscribe(() => {
      this.initProjectedOptions();
      this.updateSearchVisibility();
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Keeps projected options registered with MatSelect when the searchable variant is used.
   *
   * The sticky search field is a `mat-option` living in this component's own view, so it is a
   * native content child of the internal `MatSelect`. When it renders, Angular's own option query
   * overwrites the projected options that `initProjectedOptions` injected — leaving MatSelect with
   * only the search option, which drops any preselected value and shows an empty placeholder.
   * Re-inject the projected options whenever that native query changes.
   */
  private subscribeOnMatSelectOptionsChange(): void {
    const nativeOptions: QueryList<MatOption> = this.matSelect.options;

    const subscription = nativeOptions.changes.subscribe((): void => {
      // The search mat-option is a native child of MatSelect, so this fires when it (dis)appears.
      // Angular reassigns MatSelect.options to its native query result immediately AFTER this event,
      // clobbering the projected options. Defer the re-injection to a microtask so it runs after
      // that reassignment.
      queueMicrotask((): void => {
        if (this.hasOptionsInput || this.contentOptions.length === 0) {
          return;
        }

        if (this.matSelect.options !== this.contentOptions) {
          this.initProjectedOptions();
        }
      });
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Determines search-field visibility: an explicit `isSearchable` input wins;
   * otherwise the field auto-enables when option count exceeds the threshold.
   */
  private updateSearchVisibility(): void {
    const optionsCount = this.hasOptionsInput ? this.allOptions.length : (this.contentOptions?.length ?? 0);
    this.showSearch = this.isSearchable ?? optionsCount > this.searchOptionThreshold;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Initializes internal MatSelect options from projected content.
   */
  private initProjectedOptions(): void {
    if (this.contentOptions.length === 0) {
      this.applyProjectedOptionsFilter(this.autocompleteControl.value.trim());
      return;
    }

    this.matSelect.options = this.contentOptions;

    this.matSelect.ngAfterContentInit();
    this.applyProjectedOptionsFilter(this.autocompleteControl.value.trim());
    this.refreshSelectionDisplay();
  }

  /**
   * Re-applies the control value after mat-options are available so MatSelect shows the label on load.
   */
  private refreshSelectionDisplay(): void {
    const control: FormControl | undefined = this.control;

    if (!control || control.value === null || control.value === undefined || control.value === '') {
      return;
    }

    const currentValue: unknown = control.value;

    queueMicrotask((): void => {
      this.matSelect?.writeValue(currentValue);
      this.changeDetectorRef.markForCheck();
    });
  }

  /**
   * Filters options supplied through the `options` input.
   *
   * @param query - trimmed search text
   */
  private filterInputOptions(query: string): void {
    this.appliedFilter = query;

    const filterValue: string = query.toLowerCase();

    // A server-side search hands back an already filtered list — filtering it again would hide matches.
    this.visibleOptions =
      filterValue && !this.isServerSearch
        ? this.allOptions.filter((option: BaseDropdownInterface): boolean => option.name.toLowerCase().includes(filterValue))
        : [...this.allOptions];
    this.noOptionsMatchingFilter =
      !this.isServerSearch && filterValue.length > 0 && this.allOptions.length > 0 && this.visibleOptions.length === 0;
    this.applyRenderedOptions();
    this.changeDetectorRef.markForCheck();
    this.refreshSelectionDisplay();
  }

  /**
   * Recomputes {@link renderedOptions}, appending the control's value when the supplied list does
   * not offer it.
   *
   * Membership is tested against {@link allOptions} rather than against the filtered list on
   * purpose: a value the search text merely hid is still offered, and appending it would show it
   * twice - once in place, once pinned to the end - the moment the filter is cleared.
   *
   * The appended entry is rendered and tracked like any other option, so a value that is not an
   * option object is left alone: appending a primitive would render a blank row with no key.
   */
  private applyRenderedOptions(): void {
    if (!this.hasOptionsInput) {
      // Projected `mat-option`s own their own rendering and their own "no options" answer; this
      // path must not touch either.
      return;
    }

    const value: unknown = this.control?.value;
    const compareWith = this.compareWith ?? this.defaultCompareWith;
    const isUnmatched: boolean =
      isRenderableOption(value) && !this.allOptions.some((option: BaseDropdownInterface): boolean => compareWith(option, value));

    this.renderedOptions = isUnmatched ? [...this.visibleOptions, value as BaseDropdownInterface] : this.visibleOptions;

    // Answers what the panel actually shows: "No options available." under a rendered option would
    // contradict the row right above it. In server mode an empty list is the answer to the query
    // itself: "nothing to offer" while nothing is typed, "nothing matched" once something is. In
    // client mode it is "nothing to offer" only when there is nothing to filter at all - options the
    // search text hid already read "No options matching your filter.", and both at once is one too many.
    const isEmpty: boolean = this.renderedOptions.length === 0;

    this.noOptionsAvailable = isEmpty && (this.isServerSearch ? !this.appliedFilter : !this.appliedFilter || this.allOptions.length === 0);

    if (this.isServerSearch) {
      this.noOptionsMatchingFilter = isEmpty && this.appliedFilter.length > 0;
    }
  }

  /**
   * Filters projected mat-options for searchable selects.
   *
   * @param query - trimmed search text
   */
  private applyProjectedOptionsFilter(query: string): void {
    this.appliedFilter = query;

    if (!this.showSearch || !this.contentOptions) {
      this.noOptionsMatchingFilter = false;
      this.noOptionsAvailable = (this.contentOptions?.length ?? 0) === 0;
      this.changeDetectorRef.markForCheck();
      return;
    }

    const filterValue: string = query.toLowerCase();

    if (this.contentOptions.length === 0) {
      this.noOptionsAvailable = true;
      this.noOptionsMatchingFilter = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.noOptionsAvailable = false;
    let matchingOptionsCount = 0;
    const optgroupVisibility = new Map<HTMLElement, boolean>();

    this.contentOptions.forEach((option: MatOption): void => {
      const shouldShowOption: boolean = option.viewValue.toLowerCase().includes(filterValue);
      const optionElement = (option as MatOption & { _getHostElement?: () => HTMLElement })._getHostElement?.();

      if (optionElement) {
        optionElement.style.display = shouldShowOption ? '' : 'none';
        const optgroupElement = optionElement.closest('mat-optgroup') as HTMLElement | null;

        if (optgroupElement) {
          optgroupVisibility.set(optgroupElement, (optgroupVisibility.get(optgroupElement) ?? false) || shouldShowOption);
        }
      }

      if (shouldShowOption) {
        matchingOptionsCount += 1;
      }
    });

    // Hide a section header when all of its options are filtered out
    optgroupVisibility.forEach((hasVisibleOption: boolean, optgroupElement: HTMLElement): void => {
      optgroupElement.style.display = hasVisibleOption ? '' : 'none';
    });

    this.noOptionsMatchingFilter = filterValue.length > 0 && matchingOptionsCount === 0;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Initializes the custom trigger for MatSelect if provided
   */
  private initTrigger(): void {
    if (this.contentTrigger.length) {
      this.matSelect.customTrigger = this.contentTrigger.first;
    }
  }

  get control(): FormControl {
    return this.ngControl?.control as FormControl;
  }
}

/**
 * Whether a control value can stand in as an option of its own.
 *
 * The template renders an option's `name` and tracks it by its `id`, so only a value that carries
 * both can be appended; a primitive value - which `defaultCompareWith` supports - would render a
 * blank row under an `undefined` track key.
 *
 * @param value - the control value to test
 * @returns `true` when the value has the shape the option template needs
 */
function isRenderableOption(value: unknown): value is BaseDropdownInterface {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const option = value as Partial<BaseDropdownInterface>;

  return typeof option.id === 'string' && !!option.id && typeof option.name === 'string';
}
